import React, { useMemo } from 'react';
import { TextField, IconButton, MenuItem, SxProps, Theme } from '@mui/material';
import { Close } from '@mui/icons-material';
import Autocomplete from '@mui/material/Autocomplete';
import { AssignedCamera } from 'core/models/layout';
import { MediaDevice, CameraDevice } from 'core/models/mediaDevice';

export type CameraListProps = {
    className?: string;
    styles: Record<'select' | 'clearIndicator', SxProps<Theme>>;
    selected: { tag?: MediaDevice.Tag };
    options: CameraDevice[] | undefined;
    unplugged: boolean;
    onChange: (selected: { tag?: MediaDevice.Tag }) => void;
}

const SimpleCameralist = (props: CameraListProps) => {
    const { selected: { tag }, options = [], unplugged, styles, onChange } = props;

    const candidates = unplugged ? [...options, { tag }] : options;

    return (
        <TextField
            select
            label='Camera Slot'
            sx={styles.select}
            margin='normal'
            variant='outlined'
            fullWidth
            value={tag ?? ''}
            InputProps={{
                startAdornment: tag
                    ? <IconButton sx={styles.clearIndicator} onClick={() => onChange({})} size="large"><Close /></IconButton>
                    : undefined,
            }}
            onChange={e => onChange(candidates.find(c => c.tag === e.target.value as string) ?? {})}
        >
            {
                candidates.map((x, i) =>
                    <MenuItem key={i} value={x.tag}>{x.tag}</MenuItem>)
            }
        </TextField >
    );
};

function selectedValue(cameras: CameraDevice[] | undefined, assignedCamera: AssignedCamera) {
    return cameras && assignedCamera.tag
        ? cameras.find(c => c.tag == assignedCamera.tag) ?? { id: '', tag: assignedCamera.tag }
        : { id: '', tag: '' };
}

const AutocompleteCameralist = (props: CameraListProps) => {
    const { selected, options, onChange } = props;

    const value = useMemo(() => selectedValue(options, selected), [options, selected]);

    return (
        <Autocomplete
            options={options ?? []}
            renderOption={
                (props, camera) => {
                    return (
                        <li {...props}>
                            <div>{camera.tag}</div>
                            <div style={{ fontSize: 'small' }}>{`(id: ${camera.id})`}</div>
                        </li>
                    );
                }}
            getOptionLabel={option => option?.tag ?? ''}
            style={{ width: '50ex' }}
            renderInput={params => (
                <TextField
                    {...params}
                    label='Camera Slot'
                    margin='normal'
                    variant='outlined'
                    fullWidth
                    InputProps={{
                        ...params.InputProps,
                        type: 'text',
                    }}
                />)}
            onChange={(_, v) => onChange({ tag: v ? v.tag : undefined })}
            value={value}
        />
    );
};

export function CameraList(props: CameraListProps & { autoComplete?: boolean }) {
    const { autoComplete = false, ...otherProps } = props;
    return autoComplete ? <AutocompleteCameralist {...otherProps} /> : <SimpleCameralist {...otherProps} />;
}
